/*
 * vocal-range-capture.js — Timbrica shared vocal-range CAPTURE LEDGER.
 *
 * Why this exists
 * ---------------
 * /vocal-range used to decide your range with a bare monotonic min/max: the
 * lowest note only ever went lower, the highest only higher. A single
 * sustained-but-wrong detection therefore latched permanently and leaked into
 * the rating, the voice-type match, the PDF, the share text and the history —
 * and the only cure was a nuclear Reset that also threw away every GOOD capture
 * (prod telemetry: rage-clicks on the Reset button).
 *
 * Worse, `clarity` cannot separate a voice from a machine. Measured on the
 * shipped detector: 60 Hz mains hum reads as B1 with clarity 1.00 — a pure tone
 * is MORE periodic than a human voice, and it sounds forever, so the 380 ms
 * sustain window and its median guard are satisfied trivially. Real `outcome`
 * rows contain low=35 (= B1 = 60 Hz). The whole segment has this bug; the
 * literature's remedy is to tell the user "hit the note twice" and "check it on
 * a piano". We do both, in code.
 *
 * An f0-variance gate ("a living voice wobbles, a machine doesn't") was
 * MEASURED AND REJECTED: hum sd = 0.00 cents but a steadily sung note is only
 * sd ≈ 0.96 cents — the margin would cut down a trained singer. So the defences
 * below are CAUSAL, not statistical:
 *
 *   1. adaptive RMS floor  — a hum IS the noise floor, so it cannot rise above
 *      floor + 10 dB. Kills every continuous ambient tone (hum, fan, fridge).
 *   2. ambient-tone blacklist — tones that were already sounding while the user
 *      was silent are presumed ambient unless sung markedly louder.
 *   3. corroboration — an extreme must be produced by >= 2 SEPARATE attacks
 *      (captures closer than MERGE_GAP_MS merge into one, so a single held tone
 *      can never corroborate itself). Kills one-off transients: a cough, a
 *      chair creak, an octave error.
 *   4. the human — every capture keeps its own audio clip, so the singer can
 *      hear the note that set their range and drop it surgically.
 *
 * The gate (2026-10-01). Gate 1 used to have an absolute floor of 0.008
 * (−42 dBFS) under it, and that floor sat ABOVE the notes this tool exists to
 * find: a voice loses intensity as it descends, and with the browser's gain
 * control off (it has to be — see the blade) a quiet low note on a laptop or
 * phone mic lands around −45…−50 dBFS. Replayed on 233 real recordings
 * (VocalSet, four simulated mics): the lowest note the singer actually held
 * was captured in 53 % of runs (a quiet singer: 4 %); the status line said
 * "sound present — hold the note steadier" to somebody holding it. The floor
 * is now −54 dBFS, and the room is still answered by floor + 10 dB. What the
 * old constant did do — keep a TV or a second voice that starts AFTER the
 * calibration out of the ledger — moved to makeSingerLevel(): sounds more than
 * 20 dB below the singer's own recent level are not the singer. Measured with
 * both: the lowest held note 53 % → 82 % (quiet singer 4 % → 75 %), and a
 * background 20 dB below the singer takes the lowest note about as often as
 * before (2–5 % against 3 %).
 *
 * Pure, dependency-free, DOM-free, node-testable — the range decision is a
 * function of signals, not a side effect of a rAF loop.
 * See tests/js/vocal-range-capture.test.mjs.
 */
(function (root) {
    'use strict';

    var C = {
        MIN_RMS_ABS: 0.002,      // absolute energy gate, −54 dBFS (see "the gate" below)
        VOICE_MARGIN_DB: 10,     // a sung note must clear the measured floor by this
        TONE_PROMINENCE_DB: 12,  // a spectral peak this far above its neighbourhood is "tonal"
        TONE_TOL_CENTS: 50,      // how close to a profiled tone counts as "that tone"
        TONE_EXTRA_DB: 6,        // extra headroom demanded ON TOP of the floor at a tonal freq
        SCAN_LO_HZ: 40,          // profile band: below the lowest bass note ...
        SCAN_HI_HZ: 2100,        // ... up to the vocal ceiling
        CORROB_TOL: 1,           // semitones: captures this close are "the same note"
        MIN_CORROB: 2,           // separate attacks required to confirm an extreme
        OUTLIER_GAP: 3,          // an unconfirmed capture further than this beyond a
                                 // confirmed one is treated as an outlier, not a range
        MERGE_GAP_MS: 250,       // captures of the same note closer than this = one attack
    };

    function dbToLin(db) { return Math.pow(10, db / 20); }
    function cents(a, b) { return 1200 * Math.log2(a / b); }
    function freqToMidi(f) { return 69 + 12 * Math.log2(f / 440); }

    /* ---------------- noise profile (from a silent calibration window) -------- */

    /**
     * spectrumDb — averaged magnitude spectrum in dB (AnalyserNode-style, bin i = i*binHz).
     * rmsFloor   — linear RMS measured over the same silent window.
     * Returns { rmsFloor, gateRms, tones:[{hz, db, prominence}] }.
     */
    function makeProfile(spectrumDb, rmsFloor, binHz) {
        var gateRms = Math.max(C.MIN_RMS_ABS, rmsFloor * dbToLin(C.VOICE_MARGIN_DB));
        var tones = [];
        if (spectrumDb && spectrumDb.length && binHz > 0) {
            var lo = Math.max(1, Math.floor(C.SCAN_LO_HZ / binHz));
            var hi = Math.min(spectrumDb.length - 2, Math.ceil(C.SCAN_HI_HZ / binHz));
            // neighbourhood = ±[3..20] bins, excluding the peak's own skirt
            for (var i = lo; i <= hi; i++) {
                var v = spectrumDb[i];
                if (!(v > -Infinity)) continue;
                if (v < spectrumDb[i - 1] || v < spectrumDb[i + 1]) continue;   // local max
                var sum = 0, n = 0;
                for (var d = 3; d <= 20; d++) {
                    if (i - d >= 1) { sum += spectrumDb[i - d]; n++; }
                    if (i + d < spectrumDb.length) { sum += spectrumDb[i + d]; n++; }
                }
                if (!n) continue;
                var prom = v - (sum / n);
                if (prom < C.TONE_PROMINENCE_DB) continue;
                // Parabolic interpolation on the dB peak → sub-bin frequency. Without
                // it a 60 Hz tone in a 2.9 Hz bin can land 42 cents off, eating most of
                // the ±50-cent match window that gate 2 relies on.
                var y1 = spectrumDb[i - 1], y2 = v, y3 = spectrumDb[i + 1];
                var denom = (y1 - 2 * y2 + y3);
                var delta = denom !== 0 ? 0.5 * (y1 - y3) / denom : 0;
                if (!(delta > -1 && delta < 1)) delta = 0;
                tones.push({ hz: (i + delta) * binHz, db: v, prominence: prom });
            }
        }
        // merge tones that sit within TONE_TOL_CENTS of each other (keep the loudest)
        tones.sort(function (a, b) { return a.hz - b.hz; });
        var merged = [];
        for (var k = 0; k < tones.length; k++) {
            var last = merged[merged.length - 1];
            if (last && Math.abs(cents(tones[k].hz, last.hz)) <= C.TONE_TOL_CENTS) {
                if (tones[k].db > last.db) merged[merged.length - 1] = tones[k];
            } else merged.push(tones[k]);
        }
        return { rmsFloor: rmsFloor, gateRms: gateRms, tones: merged };
    }

    /**
     * Gate 1 + 2. Does this detected frame deserve to become a capture at all?
     * frame: { freq, rms }.  Returns { ok, reason, tone? }.
     */
    function admits(profile, frame) {
        if (!frame || !(frame.rms >= 0) || !(frame.freq > 0)) return { ok: false, reason: 'no_frame' };
        if (!profile) return { ok: frame.rms >= C.MIN_RMS_ABS, reason: frame.rms >= C.MIN_RMS_ABS ? null : 'below_floor' };
        if (frame.rms < profile.gateRms) return { ok: false, reason: 'below_floor' };
        for (var i = 0; i < profile.tones.length; i++) {
            var t = profile.tones[i];
            if (Math.abs(cents(frame.freq, t.hz)) <= C.TONE_TOL_CENTS) {
                // sounding at a frequency the room was already producing while the
                // singer was silent — demand it be sung markedly louder than the floor
                if (frame.rms < profile.gateRms * dbToLin(C.TONE_EXTRA_DB)) {
                    return { ok: false, reason: 'ambient_tone', tone: t };
                }
            }
        }
        return { ok: true, reason: null };
    }

    /* ---------------- capture ledger ----------------------------------------- */

    function activeOf(ledger) {
        var out = [];
        for (var i = 0; i < ledger.length; i++) if (!ledger[i].discarded) out.push(ledger[i]);
        return out;
    }

    /** How many SEPARATE attacks landed within CORROB_TOL semitones of `midi`. */
    function corroborationOf(ledger, midi) {
        var a = activeOf(ledger), n = 0;
        for (var i = 0; i < a.length; i++) if (Math.abs(a[i].midi - midi) <= C.CORROB_TOL) n++;
        return n;
    }

    /**
     * Append a capture, MERGING it into the previous one when it is really the
     * same continuous note (same pitch, no gap). Merging is what makes
     * corroboration mean "hit it twice" instead of "held it long enough" — the
     * whole defence against a hum corroborating itself.
     * cap: { midi, freq, clarity, rms, tStart, tEnd, clip? }
     * Returns the stored capture (new or merged-into).
     */
    function addCapture(ledger, cap) {
        for (var i = ledger.length - 1; i >= 0; i--) {
            var p = ledger[i];
            if (p.discarded) continue;
            if (Math.abs(p.midi - cap.midi) <= C.CORROB_TOL && (cap.tStart - p.tEnd) < C.MERGE_GAP_MS) {
                p.tEnd = Math.max(p.tEnd, cap.tEnd);
                if (cap.clarity > p.clarity) { p.clarity = cap.clarity; p.midi = cap.midi; p.freq = cap.freq; if (cap.clip) p.clip = cap.clip; }
                return p;
            }
            break;   // only the most recent active capture can be a continuation
        }
        var stored = {
            id: (ledger._seq = (ledger._seq || 0) + 1),
            midi: cap.midi, freq: cap.freq, clarity: cap.clarity, rms: cap.rms,
            tStart: cap.tStart, tEnd: cap.tEnd, clip: cap.clip || null, discarded: false,
        };
        ledger.push(stored);
        return stored;
    }

    function discard(ledger, id) {
        for (var i = 0; i < ledger.length; i++) if (ledger[i].id === id) { ledger[i].discarded = true; return true; }
        return false;
    }

    /**
     * Gate 3. Decide the extreme from the ledger.
     * mode: 'low' | 'high'.
     * Returns null, or { midi, confirmed, capture, pending: [outlier captures] }.
     *
     *  - the most extreme CORROBORATED capture is the anchor;
     *  - a more extreme uncorroborated capture is accepted only if it lies within
     *    OUTLIER_GAP semitones of that anchor (a plausible extension of the range);
     *  - anything further out is `pending`: shown and auditionable, but NOT counted
     *    until the singer hits it again (or accepts it by ear);
     *  - if nothing is corroborated at all we still report the most extreme capture
     *    with confirmed=false, so the tool keeps working for a one-shot user.
     */
    function chooseExtreme(ledger, mode) {
        var a = activeOf(ledger);
        if (!a.length) return null;
        var low = mode === 'low';
        a = a.slice().sort(function (x, y) { return low ? x.midi - y.midi : y.midi - x.midi; }); // most extreme first

        var anchor = null;
        for (var i = 0; i < a.length; i++) {
            if (corroborationOf(ledger, a[i].midi) >= C.MIN_CORROB) { anchor = a[i]; break; }
        }

        if (!anchor) {
            return { midi: a[0].midi, confirmed: false, capture: a[0], pending: [] };
        }

        var pending = [], chosen = anchor;
        for (var j = 0; j < a.length; j++) {
            var c = a[j];
            var beyond = low ? (anchor.midi - c.midi) : (c.midi - anchor.midi);
            if (beyond <= 0) break;                       // not more extreme than the anchor
            if (beyond <= C.OUTLIER_GAP) {                // plausible extension of the range
                if (low ? c.midi < chosen.midi : c.midi > chosen.midi) chosen = c;
            } else {
                pending.push(c);                          // too far out, unconfirmed → not counted
            }
        }
        return { midi: chosen.midi, confirmed: true, capture: chosen, pending: pending };
    }

    /* ---------------- tessitura (phase-3 sung-melody) cleaning ---------------
     * Comment #1155: "when you sing, at the end, the test gets the voice wrong
     * by octaves". Phase 3 tells the user to SING A SONG, and a sung phrase dies
     * into period-2 vocal fry at its end — which the pitch detector genuinely
     * reads an OCTAVE DOWN with high clarity. Raw frames + a 10% trim survive
     * only while artifacts stay under 10%; short phrases with long die-offs
     * (an untrained singer) push past that and the tessitura drops an octave.
     *
     * Chosen BY BAKE-OFF over synthesized melodies-with-fry (see memory): a
     * duration gate alone FAILS (sustained fry forms a valid 250-450ms "note" at
     * f0/2 — same lesson as the hum: sustained artifacts beat duration gates).
     * The winning stack is causal, each stage killing a different mechanism:
     *   1. tail gate   — fry lives in the DYING tail of a phrase: frames quieter
     *                    than 0.35× the running phrase peak are not sung notes;
     *   2. octave fold — a run 11..13 semis below the PREVIOUS run AND markedly
     *                    quieter is period-doubling, not a sung octave leap
     *                    (a real leap is sung at full voice — measured: 0 losses);
     *   3. run gate    — only frames inside stable ±1-semi runs ≥150 ms count
     *                    (sweeps onset/transition flashes).
     * Measured: harsh-case error 10.44 → 1.64 semis, octave-fails 25/25 → 5/25,
     * mild case 0.08, legit octave leaps preserved 20/20.
     *
     * frames: ({midi:int, rms:number} | null)[] in temporal order (null = no lock
     * that frame). fps — the capture rate (rAF ≈ 60).
     */
    var TESS = {
        TAIL_FRAC: 0.35,       // below this × running peak = dying tail
        PEAK_HALFLIFE_S: 1.2,  // running-peak decay
        RUN_MIN_MS: 150,       // a note must hold this long to count
        RUN_TOL: 1,            // semitones within a run
        OCT_LO: 11, OCT_HI: 13,// "an octave below" window
        OCT_RMS_FRAC: 0.6,     // and quieter than prev run × this
        TRIM: 0.10,            // final trimmed min/max (as before)
    };

    function tessRuns(frames, fps) {
        fps = fps || 60;
        // 1. tail gate
        var gated = [], peak = 0, decay = Math.pow(0.5, 1 / (TESS.PEAK_HALFLIFE_S * fps));
        for (var i = 0; i < frames.length; i++) {
            var fr = frames[i];
            peak *= decay;
            if (!fr || !(fr.rms >= 0)) { gated.push(null); continue; }
            if (fr.rms > peak) peak = fr.rms;
            gated.push(fr.rms >= peak * TESS.TAIL_FRAC ? fr : null);
        }
        // split into ±RUN_TOL-semi runs broken by nulls
        var runs = [], cur = [];
        function flush() { if (cur.length) runs.push(cur); cur = []; }
        for (var j = 0; j < gated.length; j++) {
            var g = gated[j];
            if (!g) { flush(); continue; }
            if (cur.length && Math.abs(g.midi - cur[cur.length - 1].midi) > TESS.RUN_TOL) flush();
            cur.push(g);
        }
        flush();
        // 2. octave fold, 3. run-length gate
        var minFrames = Math.round(TESS.RUN_MIN_MS / 1000 * fps);
        var med = function (a) { var s = a.map(function (x) { return x.midi; }).sort(function (x, y) { return x - y; }); return s[s.length >> 1]; };
        var meanRms = function (a) { var s = 0; for (var k = 0; k < a.length; k++) s += a[k].rms; return s / a.length; };
        var kept = [], prev = null;
        for (var r = 0; r < runs.length; r++) {
            var run = runs[r];
            if (prev) {
                var dm = med(prev) - med(run);
                if (dm >= TESS.OCT_LO && dm <= TESS.OCT_HI && meanRms(run) < meanRms(prev) * TESS.OCT_RMS_FRAC) continue;
            }
            prev = run;
            if (run.length >= minFrames) kept.push({ midi: med(run), frames: run.length, meanRms: meanRms(run), all: run });
        }
        return kept;
    }

    /**
     * Cleaned tessitura range. Returns { lo, hi, cleaned:true } or, when the
     * cleaning leaves nothing but raw frames exist (pathological — nothing was
     * stable ≥150ms above the tail gate), falls back to the legacy trimmed
     * min/max with cleaned:false so a previously-shown block never vanishes.
     */
    function tessRange(frames, fps) {
        var runs = tessRuns(frames, fps);
        var midis = [];
        for (var i = 0; i < runs.length; i++) for (var k = 0; k < runs[i].all.length; k++) midis.push(runs[i].all[k].midi);
        var cleaned = true;
        if (midis.length <= 5) {
            cleaned = false;
            for (var j = 0; j < frames.length; j++) if (frames[j]) midis.push(frames[j].midi);
        }
        if (midis.length <= 5) return null;
        midis.sort(function (a, b) { return a - b; });
        var cut = Math.floor(midis.length * TESS.TRIM);
        var t = midis.slice(cut, midis.length - cut);
        if (!t.length) return null;
        return { lo: t[0], hi: t[t.length - 1], cleaned: cleaned };
    }

    /* ---------------- the singer's own level -------------------------------
     * The second half of the gate (see the header). Tracks the loudest clearly
     * pitched frame of THIS phase, decaying with a 20 s half-life, and refuses
     * frames more than 20 dB below it. A low note sung 10–15 dB under the
     * comfortable ones passes; a TV across the room does not. Starts empty at
     * every phase, so the first notes of a phase are judged by the room alone
     * — a belted top note must not deafen the song that follows it.
     * Measured (singer + a TV or music starting after the calibration, 20 dB
     * quieter): the background took the lowest note in 4–9 % of runs with the
     * −54 dBFS floor alone, 2–5 % with this (the old −42 dBFS floor: 3 %), and
     * false captures fell from ~1 a minute to 0.2–0.4. Feed it only the
     * singer — never a frame heard while our own speaker plays.
     */
    var LEVEL = { REL_DB: 20, HALF_LIFE_MS: 20000, MIN_CLARITY: 0.8 };

    function makeSingerLevel(opts) {
        opts = opts || {};
        var rel = dbToLin(-(opts.relDb != null ? opts.relDb : LEVEL.REL_DB));
        var halfLife = opts.halfLifeMs != null ? opts.halfLifeMs : LEVEL.HALF_LIFE_MS;
        var minClar = opts.minClarity != null ? opts.minClarity : LEVEL.MIN_CLARITY;
        var peak = 0, peakT = 0;
        function level(t) { return peak > 0 ? peak * Math.pow(0.5, Math.max(0, t - peakT) / halfLife) : 0; }
        return {
            /** A frame the detector heard as the singer: rms, its clarity, time (ms). */
            hear: function (rms, clarity, t) {
                if (!(clarity >= minClar) || !(rms > 0)) return;
                if (rms > level(t)) { peak = rms; peakT = t; }
            },
            /** The RMS below which a frame is not the singer (0 until the singer is heard). */
            gate: function (t) { return level(t) * rel; },
            reset: function () { peak = 0; peakT = 0; },
        };
    }

    /* ---------------- live note display --------------------------------------
     * What the singer SEES while singing — the big note, the key on the piano,
     * the dots on the pitch graph. It used to be every frame's raw reading, 60
     * times a second. Replayed on the same 233 recordings, that put a note on
     * screen that matched NOTHING sung in the surrounding half second 32 times
     * a minute for a male voice and 17 for a female one — an octave above,
     * mostly. Somebody holding A2 watched "A3", "E4", "A2" flash past and
     * concluded, reasonably, that the tool does not hear them.
     *
     * A note is shown once it has held HOLD_MS; a jump of ten semitones or more
     * (an octave error in the making) needs OCTAVE_MS; while the voice stays
     * within HYST semitones of the shown note nothing moves (vibrato across a
     * note boundary used to flip it several times a second); a gap up to GAP_MS
     * keeps the note instead of blinking a dash. Measured with the tuned
     * detector: phantom notes 32 → 1.2 a minute (male), 17 → 0.4 (female); a
     * real step reaches the screen in ~60 ms. A median over a vibrato cycle was
     * measured too: fewer flips, but twice the phantoms and 90+ ms of lag. The
     * ledger never reads this — it is display only and cannot bend a result.
     */
    var DISP = { HOLD_MS: 50, OCTAVE_MS: 150, GAP_MS: 120, HYST: 0.7 };

    function makeNoteDisplay(opts) {
        opts = opts || {};
        var HOLD = opts.holdMs != null ? opts.holdMs : DISP.HOLD_MS;
        var OCT = opts.octaveMs != null ? opts.octaveMs : DISP.OCTAVE_MS;
        var GAP = opts.gapMs != null ? opts.gapMs : DISP.GAP_MS;
        var HYST = opts.hysteresis != null ? opts.hysteresis : DISP.HYST;
        var shown = null, cand = null, candT = 0, lastT = -Infinity;
        return {
            /** t — ms; midi — this frame's reading (fractional) or null. Returns the note to show, or null. */
            step: function (t, midi) {
                if (midi == null || midi !== midi) {
                    if (shown !== null && t - lastT > GAP) { shown = null; cand = null; }
                    return shown;
                }
                lastT = t;
                if (shown !== null && Math.abs(midi - shown) <= HYST) { cand = null; return shown; }
                var n = Math.round(midi);
                if (cand === null || Math.abs(midi - cand) > 1) { cand = n; candT = t; }
                var need = (shown !== null && Math.abs(n - shown) >= 10) ? OCT : HOLD;
                if (t - candT >= need) { shown = cand; cand = null; }
                return shown;
            },
            reset: function () { shown = null; cand = null; lastT = -Infinity; },
            shown: function () { return shown; },
        };
    }

    /* ---------------- "this is your edge" -----------------------------------
     * The ladder moves its goal a semitone past every note the singer reaches,
     * and nothing ever said "that is as far as it goes". Measured on RU prod,
     * 20 days: after their last new low people kept singing for a median 30 s
     * (p90: over three minutes), and 596 of the 2 903 who sang in the low phase
     * never reached the high one. The bug report behind this block came from
     * somebody who spent two minutes on a goal one semitone under his floor.
     *
     * settled(): MIN_ATTEMPTS separate attacks AND MIN_MS without the extreme
     * moving outward. It only SAYS so — the goal stays, and the next attack
     * that goes further reopens it (a third of long streaks in prod still gain
     * a semitone later, so nobody is cut off).
     */
    var EDGE = { MIN_ATTEMPTS: 4, MIN_MS: 20000 };

    function makeEdgeWatch(mode, opts) {
        opts = opts || {};
        var MIN_A = opts.minAttempts != null ? opts.minAttempts : EDGE.MIN_ATTEMPTS;
        var MIN_T = opts.minMs != null ? opts.minMs : EDGE.MIN_MS;
        var low = mode === 'low';
        var best = null, attempts = 0, sinceT = 0;
        return {
            /** Start (or restart, e.g. after a discard) from the extreme the ledger reports now. */
            begin: function (extremeMidi, t) { best = extremeMidi == null ? null : extremeMidi; attempts = 0; sinceT = t; },
            /** After every NEW capture, with the extreme the ledger reports after it. */
            capture: function (extremeMidi, t) {
                if (extremeMidi == null) return;
                if (best === null || (low ? extremeMidi < best : extremeMidi > best)) { best = extremeMidi; attempts = 0; sinceT = t; }
                else attempts++;
            },
            settled: function (t) { return best !== null && attempts >= MIN_A && (t - sinceT) >= MIN_T; },
            attempts: function () { return attempts; },
            sinceMs: function (t) { return t - sinceT; },
            best: function () { return best; },
        };
    }

    var api = {
        CONST: C, TESS: TESS, LEVEL: LEVEL, DISP: DISP, EDGE: EDGE,
        makeProfile: makeProfile, admits: admits,
        tessRuns: tessRuns, tessRange: tessRange,
        addCapture: addCapture, discard: discard,
        corroborationOf: corroborationOf, chooseExtreme: chooseExtreme,
        activeOf: activeOf, freqToMidi: freqToMidi,
        makeSingerLevel: makeSingerLevel, makeNoteDisplay: makeNoteDisplay, makeEdgeWatch: makeEdgeWatch,
        VERSION: '1.2.0',
    };
    root.VocalCapture = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
